import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';

import { BackendTable } from '@/components/backend/table/BackendTable';
import { UserRoleFilter } from '@/components/backend/table/filters/UserRoleFilter';
import { UserStatusFilter } from '@/components/backend/table/filters/UserStatusFilter';
import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { I18N_DE } from '@/i18n/de';
import type { User } from '@/modules/api';
import { backendApiUsersFindAllOptions } from '@/modules/api/@tanstack/react-query.gen';
import { exactArraySelectionFilter } from '@/utils/exactArraySelectionFilter';
import { i18nSortingFn } from '@/utils/i18nSortingFn';

export const Route = createFileRoute('/verwaltung/_global/benutzerinnen/')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

const columnHelper = createColumnHelper<User>();

const columns = [
  columnHelper.accessor('firstName', {
    header: 'Vorname',
    enableColumnFilter: false,
  }),
  columnHelper.accessor('lastName', {
    header: 'Nachname',
    enableColumnFilter: false,
  }),
  columnHelper.accessor('email', {
    header: 'E-Mail',
    enableColumnFilter: false,
  }),
  columnHelper.accessor('role', {
    header: 'Rolle',
    enableGlobalFilter: false,
    filterFn: exactArraySelectionFilter<User['role'], User>,
    sortingFn: i18nSortingFn(I18N_DE.UserRole),
    cell: (info) => I18N_DE.UserRole[info.getValue()],
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    enableGlobalFilter: false,
    filterFn: exactArraySelectionFilter<User['status'], User>,
    sortingFn: i18nSortingFn(I18N_DE.UserStatus),
    cell: (info) => I18N_DE.UserStatus[info.getValue()],
  }),
  columnHelper.accessor((row) => row.adminLevels?.filter((adminLevelUser) => adminLevelUser.status === 'INACTIVE'), {
    header: 'Inaktive Zuordnungen',
    id: 'inactiveAdminLevelUsers',
    enableColumnFilter: false,
    cell: (info) => info.getValue()?.length ?? 0,
  }),
] as ColumnDef<User>[];

function RouteComponent() {
  const { data, isFetching, error } = useQuery(backendApiUsersFindAllOptions());

  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    <BackendTable
      fullHeight
      columns={columns}
      data={data}
      filters={{ role: UserRoleFilter, status: UserStatusFilter }}
      getRowId={(row) => row.id}
      isFetching={isFetching}
      onClickNavigate={(row) => `/verwaltung/benutzerinnen/${row.id}`}
      searchInputPlaceholder="Suche nach Vorname, Nachname oder E-Mail"
      subject="Benutzer*innen"
    />
  );
}
